import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Loader2, RefreshCw, Clock, Users, ChevronLeft, ChevronRight,
  Building2, CheckCircle, XCircle, AlertCircle, Search
} from 'lucide-react';
import { toast } from 'sonner';

const BOOKABLE_TYPE_LABELS = {
  meeting_room: 'Meeting Room',
  conference_room: 'Conference Room',
  day_pass: 'Day Pass',
  dedicated_desk: 'Dedicated Desk',
  event_space: 'Event Space',
  virtual_office: 'Virtual Office',
};

const AvailabilityView = ({ venues: propVenues }) => {
  const { api } = useAuth();
  const { isDark } = useTheme();

  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedVenue, setSelectedVenue] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (selectedVenue !== 'all') params.append('venue_id', selectedVenue);
      const res = await api.get(`/availability/dashboard?${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load availability', err);
      toast.error('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  }, [api, selectedDate, selectedVenue]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const changeDate = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Generate hour labels from the data
  const hourLabels = useMemo(() => {
    if (!data?.venues?.length) return [];
    let minStart = 24, maxEnd = 0;
    for (const v of data.venues) {
      const s = parseInt(v.opening_time?.split(':')[0] || '9');
      const e = parseInt(v.closing_time?.split(':')[0] || '21');
      if (s < minStart) minStart = s;
      if (e > maxEnd) maxEnd = e;
    }
    const labels = [];
    for (let h = minStart; h < maxEnd; h++) {
      labels.push(`${h.toString().padStart(2, '0')}:00`);
    }
    return labels;
  }, [data]);

  // Apply filters
  const filteredVenues = useMemo(() => {
    if (!data?.venues) return [];
    return data.venues.map(v => ({
      ...v,
      bookables: v.bookables.filter(b => {
        if (typeFilter !== 'all' && b.bookable_type !== typeFilter) return false;
        if (searchQuery && !b.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
      })
    })).filter(v => v.bookables.length > 0);
  }, [data, typeFilter, searchQuery]);

  // Compute totals
  const totals = useMemo(() => {
    if (!filteredVenues.length) return { total: 0, available: 0, booked: 0, pct: 0 };
    let total = 0, available = 0;
    for (const v of filteredVenues) {
      for (const b of v.bookables) {
        total += b.total_slots;
        available += b.available_slots;
      }
    }
    const booked = total - available;
    return { total, available, booked, pct: total > 0 ? Math.round((available / total) * 100) : 0 };
  }, [filteredVenues]);

  const cardBg = isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]';
  const innerBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const inputBg = isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]';

  const formatDateDisplay = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isToday = selectedDate === today;

  // Current hour marker
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  // Get all unique bookable types from data
  const availableTypes = useMemo(() => {
    if (!data?.venues) return [];
    const types = new Set();
    for (const v of data.venues) {
      for (const b of v.bookables) {
        types.add(b.bookable_type);
      }
    }
    return Array.from(types);
  }, [data]);

  // Which venues to show in filter
  const venueList = propVenues || data?.venues?.map(v => ({ id: v.venue_id, name: v.venue_name })) || [];

  return (
    <div className="space-y-5" data-testid="availability-view">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Navigation */}
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => changeDate(-1)} data-testid="avail-prev-date">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={`w-40 h-9 text-sm ${inputBg}`}
            data-testid="avail-date-picker"
          />
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => changeDate(1)} data-testid="avail-next-date">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setSelectedDate(today)}>
              Today
            </Button>
          )}
        </div>

        {/* Venue Filter */}
        <Select value={selectedVenue} onValueChange={setSelectedVenue}>
          <SelectTrigger className={`w-48 h-9 text-sm ${inputBg}`} data-testid="avail-venue-filter">
            <SelectValue placeholder="All Venues" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Venues</SelectItem>
            {venueList.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Type Filter */}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className={`w-44 h-9 text-sm ${inputBg}`} data-testid="avail-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {availableTypes.map(t => (
              <SelectItem key={t} value={t}>{BOOKABLE_TYPE_LABELS[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search space..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-8 w-44 h-9 text-sm ${inputBg}`}
            data-testid="avail-search"
          />
        </div>

        {/* Refresh */}
        <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={fetchAvailability} data-testid="avail-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={cardBg}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Slots</p>
              <p className="text-xl font-bold" data-testid="avail-total-slots">{totals.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={cardBg}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Available</p>
              <p className="text-xl font-bold text-emerald-500" data-testid="avail-available-slots">{totals.available}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={cardBg}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Booked</p>
              <p className="text-xl font-bold text-red-500" data-testid="avail-booked-slots">{totals.booked}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={cardBg}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Availability</p>
              <p className="text-xl font-bold" data-testid="avail-pct">{totals.pct}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Label */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-muted-foreground">
          {formatDateDisplay(selectedDate)}
          {isToday && <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Today</span>}
        </h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-emerald-500/20 border border-emerald-500/40 inline-block" /> Available
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-500/30 border border-red-500/50 inline-block" /> Booked
          </span>
          {isToday && (
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-muted border border-muted-foreground/20 inline-block" /> Past
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredVenues.length === 0 && (
        <Card className={cardBg}>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No spaces found</p>
            <p className="text-sm mt-1">Try adjusting your filters or date</p>
          </CardContent>
        </Card>
      )}

      {/* Timeline Grid per Venue */}
      {!loading && filteredVenues.map(venue => (
        <Card key={venue.venue_id} className={`${cardBg} overflow-hidden`} data-testid={`avail-venue-${venue.venue_id}`}>
          {/* Venue Header */}
          <div className={`px-4 py-3 border-b ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'} flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">{venue.venue_name}</span>
              {venue.region && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">{venue.region}</Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {venue.opening_time} — {venue.closing_time}
            </span>
          </div>

          {/* Timeline */}
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Hour Headers */}
              <div className="flex border-b border-border/50">
                <div className="w-48 min-w-[192px] shrink-0 px-3 py-2 text-xs font-medium text-muted-foreground">
                  Space
                </div>
                <div className="flex-1 flex">
                  {hourLabels.map(label => {
                    const hour = parseInt(label.split(':')[0]);
                    const isPast = isToday && hour < currentHour;
                    const isCurrent = isToday && hour === currentHour;
                    return (
                      <div
                        key={label}
                        className={`flex-1 text-center py-2 text-[10px] font-medium border-l border-border/30 ${
                          isCurrent ? 'text-primary font-bold' : isPast ? 'text-muted-foreground/50' : 'text-muted-foreground'
                        }`}
                      >
                        {hour > 12 ? `${hour - 12}P` : hour === 12 ? '12P' : `${hour}A`}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bookable Rows */}
              {venue.bookables.map((bookable) => {
                const occupancyPct = bookable.total_slots > 0
                  ? Math.round(((bookable.total_slots - bookable.available_slots) / bookable.total_slots) * 100)
                  : 0;

                return (
                  <div
                    key={bookable.id}
                    className={`flex border-b border-border/30 hover:${innerBg} transition-colors`}
                    data-testid={`avail-row-${bookable.id}`}
                  >
                    {/* Space Info */}
                    <div className="w-48 min-w-[192px] shrink-0 px-3 py-2.5 flex flex-col justify-center border-r border-border/30">
                      <span className="text-sm font-medium truncate">{bookable.name}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground capitalize">
                          {BOOKABLE_TYPE_LABELS[bookable.bookable_type] || bookable.bookable_type}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Users className="w-2.5 h-2.5" />{bookable.capacity}
                        </span>
                      </div>
                      <span className="text-[11px] font-semibold text-primary mt-0.5">
                        {bookable.hourly_rate > 0 ? `₹${bookable.hourly_rate.toLocaleString('en-IN')}/${bookable.booking_mode === 'daily' ? 'day' : bookable.booking_mode === 'monthly' ? 'mo' : 'hr'}` : 'Free'}
                      </span>
                      {/* Mini occupancy bar */}
                      <div className="mt-1.5 w-full h-1 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            occupancyPct > 80 ? 'bg-red-500' : occupancyPct > 50 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${occupancyPct}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-muted-foreground mt-0.5">
                        {bookable.available_slots}/{bookable.total_slots} free
                      </span>
                    </div>

                    {/* Slots */}
                    <div className="flex-1 flex">
                      {bookable.slots.map((slot, idx) => {
                        const slotHour = parseInt(slot.start.split(':')[0]);
                        const isPast = isToday && slotHour < currentHour;
                        const isCurrent = isToday && slotHour === currentHour;

                        let bg, border, title;
                        if (isPast && slot.available) {
                          bg = isDark ? 'bg-[#1a1c20]' : 'bg-gray-100';
                          border = 'border-border/20';
                          title = `${slot.start}-${slot.end}: Past`;
                        } else if (!slot.available) {
                          bg = isDark ? 'bg-red-500/15' : 'bg-red-50';
                          border = 'border-red-500/30';
                          title = `${slot.start}-${slot.end}: Booked${slot.booking?.is_internal ? ' (Internal)' : ''}`;
                        } else {
                          bg = isDark ? 'bg-emerald-500/10' : 'bg-emerald-50';
                          border = 'border-emerald-500/20';
                          title = `${slot.start}-${slot.end}: Available`;
                        }

                        return (
                          <div
                            key={idx}
                            className={`flex-1 ${bg} border-l ${border} flex items-center justify-center transition-colors relative group cursor-default ${
                              isCurrent ? 'ring-1 ring-primary/50 ring-inset' : ''
                            }`}
                            title={title}
                            data-testid={`slot-${bookable.id}-${slot.start}`}
                          >
                            {!slot.available && (
                              <div className={`w-2 h-2 rounded-full ${
                                slot.booking?.is_internal
                                  ? 'bg-amber-400'
                                  : 'bg-red-400'
                              }`} />
                            )}
                            {slot.available && !isPast && (
                              <span className="text-[10px] font-semibold text-emerald-600/80">
                                ₹{bookable.hourly_rate > 0 ? bookable.hourly_rate.toLocaleString('en-IN') : '0'}
                              </span>
                            )}
                            {/* Tooltip on hover */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-20 pointer-events-none">
                              <div className={`px-2.5 py-1.5 rounded text-[10px] whitespace-nowrap shadow-lg ${
                                isDark ? 'bg-[#1e2128] text-gray-200' : 'bg-white text-gray-800 border border-gray-200'
                              }`}>
                                <span className="font-medium">{slot.start} — {slot.end}</span>
                                <br />
                                {isPast && slot.available ? 'Past' :
                                 !slot.available ? (slot.booking?.is_internal ? 'Internal Booking' : 'Booked') :
                                 'Available'}
                                {slot.available && !isPast && bookable.hourly_rate > 0 && (
                                  <span className="block font-semibold text-primary mt-0.5">
                                    ₹{bookable.hourly_rate.toLocaleString('en-IN')}/{bookable.booking_mode === 'daily' ? 'day' : bookable.booking_mode === 'monthly' ? 'mo' : 'hr'} + GST
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AvailabilityView;
